import React from 'react';
import DOMPurify from 'dompurify';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Send, Filter, Info, Trash2, Edit } from 'lucide-react';
import RichTextEditor from '../../components/RichTextEditor';
import FilePickerInput from '../../components/FilePickerInput';
import YouTubeEmbed from '../../components/YouTubeEmbed';
import LessonFiles from '../../components/LessonFiles';
import MessageFiles from '../../components/MessageFiles';

const getFullName = (user) => {
  if (!user) return '';
  const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return full || user.username || '';
};

export default function PrivateTab({ ctx }) {
  const {
    user,
    token,
    classes,
    lessons,
    filteredLessons,
    lessonFilter,
    setLessonFilter,
    boardContent,
    setBoardContent,
    boardFiles,
    setBoardFiles,
    boardFilter,
    setBoardFilter,
    filteredBoardMessages,
    boardMessages,
    handleSendBoardMessage,
    privateContent,
    setPrivateContent,
    privateRecipient,
    setPrivateRecipient,
    privateFiles,
    setPrivateFiles,
    privateFilter,
    setPrivateFilter,
    filteredPrivateMessages,
    privateMessages,
    handleSendPrivateMessage,
    recipients,
    getSenderRoleColor,
    handleEditMessage,
    handleDeleteMessage,
  } = ctx;
  return (
          <TabsContent value="private" data-testid="private-content">
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">Изпращане на лично съобщение</h2>
                <Card className="p-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="recipient">Получател</Label>
                      <Select value={privateRecipient} onValueChange={setPrivateRecipient}>
                        <SelectTrigger>
                          <SelectValue placeholder="Изберете получател" />
                        </SelectTrigger>
                        <SelectContent className="max-h-80">
                          {recipients.filter(r => r.role === 'teacher').length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0">Учители</div>
                              {recipients.filter(r => r.role === 'teacher').map(r => (
                                <SelectItem key={r.user_id} value={r.user_id} className="pl-6">
                                  {getFullName(r)}
                                </SelectItem>
                              ))}
                            </>
                          )}
                          {recipients.filter(r => r.role === 'administrator').length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0 mt-1">Администратори</div>
                              {recipients.filter(r => r.role === 'administrator').map(r => (
                                <SelectItem key={r.user_id} value={r.user_id} className="pl-6">
                                  {getFullName(r)}
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <RichTextEditor
                      value={privateContent}
                      onChange={setPrivateContent}
                      placeholder="Напишете вашето съобщение..."
                    />
                    <FilePickerInput
                      files={privateFiles}
                      onChange={setPrivateFiles}
                      label="Прикачи файлове"
                    />
                    <Button
                      onClick={handleSendPrivateMessage}
                      data-testid="send-private-btn"
                      className="w-full bg-[#00966E] hover:bg-[#007a59] text-white flex items-center justify-center gap-2"
                    >
                      <Send className="h-4 w-4" />
                      Изпращане
                    </Button>
                  </div>
                </Card>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-slate-900">Получени лични съобщения</h2>
                  <Select value={privateFilter} onValueChange={setPrivateFilter}>
                    <SelectTrigger className="w-[140px] sm:w-[180px]">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Всички</SelectItem>
                      <SelectItem value="teacher">Учители</SelectItem>
                      <SelectItem value="admin">Администратори</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {filteredPrivateMessages.length === 0 ? (
                  <Card className="p-8 text-center text-slate-500">
                    Няма лични съобщения
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {filteredPrivateMessages.map((msg) => (
                      <Card key={msg.message_id} data-testid="private-message-card" className="p-4 border border-slate-200">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{msg.sender_name}</span>
                            <Badge className={getSenderRoleColor(msg.sender_role)}>
                              {msg.sender_role === 'teacher' ? 'Учител' : msg.sender_role === 'administrator' ? 'Администратор' : 'Ученик'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">
                              {new Date(msg.created_at).toLocaleDateString('bg-BG')}
                            </span>
                            {msg.sender_id === user.user_id && (
                              <>
                                <Button onClick={() => handleEditMessage(msg)} variant="ghost" size="sm"
                                  data-testid="edit-private-message-btn"
                                  className="text-slate-500 hover:text-[#00966E] hover:bg-green-50 h-6 w-6 p-0"
                                ><Edit className="h-3 w-3" /></Button>
                                <Button
                                  onClick={() => handleDeleteMessage(msg.message_id)}
                                  variant="ghost"
                                  size="sm"
                                  data-testid="delete-message-btn"
                                  className="text-[#D01C1F] hover:text-[#a81619] hover:bg-red-50 h-6 w-6 p-0"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        <p className="text-slate-700 mb-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content) }} />
                        <YouTubeEmbed html={msg.content} />
                        <MessageFiles msg={msg} token={token} />
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
  );
}
